/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import { X, ChevronDown, Loader2 } from "lucide-react";
import { STAGES, VETTING_STAGE_LABELS } from "../../../utils/constants";

const MoveDialog = ({ isOpen, onClose, onConfirm, currentStage }) => {
  const [targetStage, setTargetStage] = useState(
    STAGES[STAGES.indexOf(currentStage) + 1] || "Active",
  );
  const [note, setNote] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setTargetStage(STAGES[STAGES.indexOf(currentStage) + 1] || "Active");
      setNote("");
      setIsLoading(false);
    }
  }, [isOpen, currentStage]);

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  const handleConfirm = async () => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      await onConfirm(targetStage, note);
      // Reset state after successful confirmation
      setTargetStage(STAGES[STAGES.indexOf(currentStage) + 1] || "Active");
      setNote("");
    } catch (error) {
      // Keep state if there's an error
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in-95 duration-200">
        <div className="p-6 border-b border-gray-100 dark:border-dark-border flex justify-between items-center">
          <h3 className="text-lg font-bold text-text dark:text-dark-text">
            Move to Stage
          </h3>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className={`${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <X size={20} className="text-gray-400 dark:text-dark-muted" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
              Select Target Stage
            </label>
            <div className="relative">
              <select
                value={targetStage}
                onChange={(e) => setTargetStage(e.target.value)}
                disabled={isLoading}
                className="w-full p-3 bg-accent dark:bg-dark-border border border-gray-200 dark:border-dark-border rounded-xl appearance-none outline-none focus:ring-2 focus:ring-primary text-text dark:text-dark-text disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {STAGES.map((s) => (
                  <option key={s} value={s}>
                    {VETTING_STAGE_LABELS[s] || s}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={16}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-dark-muted pointer-events-none"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
              Note (Optional)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={isLoading}
              rows={3}
              className="w-full p-3 bg-accent dark:bg-dark-border border border-gray-200 dark:border-dark-border rounded-xl outline-none focus:ring-2 focus:ring-primary resize-none text-text dark:text-dark-text disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder="Add internal note..."
            />
          </div>
        </div>
        <div className="p-6 pt-2 flex gap-3">
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="flex-1 py-2.5 bg-accent dark:bg-dark-border text-text dark:text-dark-text rounded-xl font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading}
            className="flex-1 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl font-bold transition-colors shadow-lg shadow-primary/20 dark:shadow-none disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Moving...
              </>
            ) : (
              "Move"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MoveDialog;
